import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/supabase';
import { 
  isRateLimited, 
  recordAttempt, 
  clearRateLimit, 
  startSessionMonitor,
  getPublicErrorMessage,
  ADMIN_PREFIX 
} from '@/lib/security';
import { toast } from 'sonner';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastLogin, setLastLogin] = useState(null);

  // Session timeout handler
  const handleSessionTimeout = useCallback(async () => {
    toast.error('Session expired. Please log in again.');
    await signOut();
  }, []);

  useEffect(() => {
    // Check for existing session
    const initAuth = async () => {
      try {
        const session = await authApi.getSession();
        setUser(session?.user ?? null);
        
        // If user is logged in, start session monitoring
        if (session?.user) {
          const cleanup = startSessionMonitor(handleSessionTimeout);
          return cleanup;
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        setLoading(false);
      }
    };

    const cleanupPromise = initAuth();

    // Listen for auth changes
    const { data: { subscription } } = authApi.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      // Cleanup session monitor if it exists
      cleanupPromise?.then(cleanup => cleanup?.());
    };
  }, [handleSessionTimeout]);

  const signIn = async (email, password) => {
    // Rate limiting check
    const rateLimitKey = `login_${email.toLowerCase()}`;
    const rateLimitCheck = isRateLimited(rateLimitKey, 5, 15 * 60 * 1000);
    
    if (rateLimitCheck.limited) {
      throw new Error(rateLimitCheck.message);
    }

    try {
      // Record attempt before trying
      recordAttempt(rateLimitKey);
      
      const { user } = await authApi.signIn(email, password);
      
      // Clear rate limit on successful login
      clearRateLimit(rateLimitKey);
      
      // Store last login time
      setLastLogin(new Date().toISOString());
      localStorage.setItem('lastLogin', new Date().toISOString());
      
      setUser(user);
      
      // Start session monitoring
      startSessionMonitor(handleSessionTimeout);
      
      return user;
    } catch (error) {
      // Throw sanitized error message
      throw new Error(getPublicErrorMessage(error));
    }
  };

  const signOut = async () => {
    try {
      await authApi.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('lastLogin');
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user,
    lastLogin: lastLogin || localStorage.getItem('lastLogin')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
